import { Code, ConnectError } from '@connectrpc/connect'
import i18n from '@/i18n'

/** Server trailer keys (MafiaGo-API internal/domain/error_codes.go). */
export const TRAILER_ERROR_CODE = 'x-mafiago-error-code'
export const TRAILER_PARAM_PREFIX = 'x-mafiago-param-'

/**
 * Codes this client has copy for, in the `errors` namespace. Anything else
 * renders the generic message — a shipped build will meet codes it predates.
 */
export const KNOWN_ERROR_CODES = [
  'web_no_account',
  'web_setup_incomplete',
  'web_scope_denied',
  'val_rate_limit',
  'account_deleted',
  'account_banned',
  'account_suspended',
  'session_replaced',
  'not_found',
  'unauthenticated',
  'unavailable',
  'firebase_cancelled',
  'firebase_popup_blocked',
  'firebase_invalid_credential',
  'firebase_too_many_requests',
  'firebase_network',
  'firebase_not_configured',
  'unknown',
] as const

export type KnownErrorCode = (typeof KNOWN_ERROR_CODES)[number]

export interface GameError {
  /** The stable server code (or a client-derived one); 'unknown' when neither. */
  code: string
  params: Record<string, string>
  /** Localized, ready to render. */
  message: string
  /** The gRPC status, when the error came off the wire. */
  grpcCode?: Code
}

/** Collapses the server's aliases onto the one key we translate. */
function canonical(code: string): string {
  switch (code) {
    case 'chat_rate_limited':
    case 'rate_limited':
    case 'rate_limit':
      return 'val_rate_limit'
    default:
      return code
  }
}

function derivedCode(grpcCode: Code): string {
  switch (grpcCode) {
    case Code.ResourceExhausted:
      return 'val_rate_limit'
    case Code.Unauthenticated:
      return 'unauthenticated'
    case Code.Unavailable:
    case Code.DeadlineExceeded:
      return 'unavailable'
    case Code.PermissionDenied:
      return 'web_scope_denied'
    case Code.NotFound:
      return 'not_found'
    default:
      return 'unknown'
  }
}

export function localizeError(code: string, params: Record<string, string> = {}): string {
  const generic = i18n.t('unknown', { ns: 'errors' })
  if (!(KNOWN_ERROR_CODES as readonly string[]).includes(code)) return generic
  return i18n.t(code, { ns: 'errors', ...params, defaultValue: generic })
}

/**
 * Normalises anything thrown by an RPC (or by Firebase) into a GameError.
 *
 * The code comes from the `x-mafiago-error-code` trailer and the params from
 * `x-mafiago-param-*`. ConnectError.metadata already merges response headers
 * and trailers, so a trailers-only gRPC-Web response (where the trailer rides
 * in the HTTP headers) is covered too — PROVIDED the ingress exposes those
 * headers via CORS (cors-expose-headers in MafiaGo-IaC).
 */
export function toGameError(err: unknown): GameError {
  if (err instanceof ConnectError) {
    const params: Record<string, string> = {}
    let code = ''
    err.metadata.forEach((value, key) => {
      const k = key.toLowerCase()
      if (k === TRAILER_ERROR_CODE) code = value
      else if (k.startsWith(TRAILER_PARAM_PREFIX)) params[k.slice(TRAILER_PARAM_PREFIX.length)] = value
    })
    code = canonical(code || derivedCode(err.code))
    return { code, params, message: localizeError(code, params), grpcCode: err.code }
  }
  const fb = firebaseCode(err)
  if (fb) return { code: fb, params: {}, message: localizeError(fb) }
  if (err instanceof TypeError) {
    // fetch() rejects with a TypeError when the network is down or CORS fails.
    return { code: 'unavailable', params: {}, message: localizeError('unavailable') }
  }
  return { code: 'unknown', params: {}, message: localizeError('unknown') }
}

function firebaseCode(err: unknown): string | null {
  if (!err || typeof err !== 'object' || !('code' in err)) return null
  const raw = (err as { code?: unknown }).code
  if (typeof raw !== 'string' || !raw.startsWith('auth/')) return null
  switch (raw) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
    case 'auth/user-cancelled':
      return 'firebase_cancelled'
    case 'auth/popup-blocked':
      return 'firebase_popup_blocked'
    case 'auth/invalid-credential':
    case 'auth/invalid-email':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/missing-password':
    case 'auth/user-disabled':
      return 'firebase_invalid_credential'
    case 'auth/too-many-requests':
      return 'firebase_too_many_requests'
    case 'auth/network-request-failed':
      return 'firebase_network'
    case 'auth/not-configured':
      return 'firebase_not_configured'
    default:
      return 'unknown'
  }
}

/**
 * A newer sign-in on another browser (or tab) replaced this session. The
 * server keeps one web session per player, so the answer is to stop — never
 * to sign in again silently, which would displace the other browser, which
 * would do the same back.
 */
export function isSessionReplaced(err: unknown): boolean {
  return err instanceof ConnectError && toGameError(err).code === 'session_replaced'
}

/** Where a sign-in refusal sends the player, or null to stay on /login. */
export function routeForSignInError(e: GameError): string | null {
  switch (e.code) {
    case 'web_no_account':
      return '/no-account'
    case 'web_setup_incomplete': {
      const step = e.params.step === 'age' ? 'age' : 'faction'
      return `/setup-incomplete?step=${step}`
    }
    default:
      return null
  }
}
